// src/pages/admin/AllUsers.js
import React, { useState, useEffect } from 'react';
import { message, Modal, Form, Input, Select, Button, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import UserTable from './UserTable';
import UserModal from './UserModal';

const { Option } = Select;
const { Title } = Typography;

const AllUsers = ({ onLogout }) => { // Added onLogout prop
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Enhanced sample data with role-specific details
  const initialUsers = [
    {
      id: 1,
      name: 'John Doe',
      role: 'STUDENT',
      status: 'Active',
      email: 'john@example.com',
      phone: '123-456-7890',
      createdAt: '2023-01-01',
      courses: [
        { id: 101, title: 'AI Fundamentals', status: 'Completed', progress: 100 },
        { id: 102, title: 'Cybersecurity Basics', status: 'In Progress', progress: 60 },
      ],
      challenges: [
        { id: 201, title: 'AI Image Recognition', status: 'Submitted', score: 85 },
        { id: 202, title: 'Cybersecurity Challenge', status: 'Pending', score: null },
      ],
      mentors: [{ id: 2, name: 'Jane Smith', expertise: 'AI' }],
      skills: ['Python', 'Machine Learning'],
      projects: [{ id: 301, title: 'AI Chatbot', status: 'Completed' }],
    },
    {
      id: 2,
      name: 'Jane Smith',
      role: 'MENTOR',
      status: 'Active',
      email: 'jane@example.com',
      phone: '234-567-8901',
      createdAt: '2023-02-01',
      mentees: [{ id: 1, name: 'John Doe', progress: 'Good' }],
      coursesTaught: [{ id: 101, title: 'AI Fundamentals', studentsEnrolled: 15 }],
    },
    {
      id: 3,
      name: 'Bob Johnson',
      role: 'VENDOR',
      status: 'Pending',
      email: 'bob@example.com',
      phone: '345-678-9012',
      createdAt: '2023-03-01',
      products: [{ id: 401, name: 'AI Chatbot API', status: 'Approved' }],
    },
  ];

  useEffect(() => {
    setLoading(true);
    // Simulate API fetch
    setTimeout(() => {
      setUsers(initialUsers);
      setFilteredUsers(initialUsers);
      setLoading(false);
    }, 500);

    // Placeholder for real API call
    /*
    api.get('/users')
      .then((response) => {
        setUsers(response.data);
        setFilteredUsers(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching users:', error);
        message.error('Failed to load users');
        setLoading(false);
      });
    */
  }, []);

  const handleSearch = (value) => {
    const filtered = users.filter(
      (user) =>
        user.name.toLowerCase().includes(value.toLowerCase()) ||
        user.email.toLowerCase().includes(value.toLowerCase()) ||
        user.role.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const handleSort = (column, order) => {
    setSortColumn(column);
    setSortOrder(order);
    const sorted = [...filteredUsers].sort((a, b) => {
      const valueA = a[column]?.toString().toLowerCase() || '';
      const valueB = b[column]?.toString().toLowerCase() || '';
      return order === 'ascend' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
    setFilteredUsers(sorted);
  };

  const handleDeactivateUser = (userId) => {
    const user = users.find((u) => u.id === userId);
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    Modal.confirm({
      title: `${newStatus === 'Inactive' ? 'Deactivate' : 'Activate'} User`,
      content: `Are you sure you want to ${newStatus.toLowerCase()} "${user.name}"?`,
      onOk: () => {
        const updatedUsers = users.map((u) =>
          u.id === userId ? { ...u, status: newStatus } : u
        );
        setUsers(updatedUsers);
        setFilteredUsers(updatedUsers);
        message.success(`User "${user.name}" ${newStatus.toLowerCase()}d successfully`);
      },
    });
  };

  const handleEditUser = (userId) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const handleAddUser = () => {
    setIsAddModalVisible(true);
  };

  const handleAddUserSubmit = (values) => {
    const newUser = {
      id: users.length + 1, // Simple ID increment for demo; use UUID or backend ID in production
      name: values.name,
      email: values.email,
      role: values.role,
      phone: values.phone,
      status: values.status,
      createdAt: new Date().toISOString().split('T')[0],
    };
    const updatedUsers = [...users, newUser];
    setUsers(updatedUsers);
    setFilteredUsers(updatedUsers);
    setIsAddModalVisible(false);
    form.resetFields();
    message.success(`User "${newUser.name}" added successfully`);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewModalVisible(true);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
    if (sorter.field && sorter.order) {
      handleSort(sorter.field, sorter.order);
    }
  };

  return (
    <div style={{ padding: '24px', background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
      <Title level={2} style={{ marginBottom: 16 }}>All Users</Title>
      <UserTable
        users={filteredUsers}
        loading={loading}
        currentPage={currentPage}
        pageSize={pageSize}
        handleSearch={handleSearch}
        handleViewUser={handleViewUser}
        handleEditUser={handleEditUser}
        handleDeactivateUser={handleDeactivateUser}
        handleAddUser={handleAddUser}
        handleTableChange={handleTableChange}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
      />
      <UserModal
        visible={isViewModalVisible}
        onClose={() => setIsViewModalVisible(false)}
        user={selectedUser}
        onEdit={() => {
          setIsViewModalVisible(false);
          handleEditUser(selectedUser?.id);
        }}
      />
      <Modal
        title="Add New User"
        open={isAddModalVisible} // Changed from 'visible' for Ant Design v5 compatibility
        onCancel={() => {
          setIsAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUserSubmit}
          initialValues={{ status: 'Active' }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter the user’s name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter the user’s email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder="Select role">
              <Option value="STUDENT">Student</Option>
              <Option value="MENTOR">Mentor</Option>
              <Option value="VENDOR">Vendor</Option>
              <Option value="ADMIN">Admin</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[{ required: true, message: 'Please enter the user’s phone number' }]}
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status">
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
              <Option value="Pending">Pending</Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Add User</Button>
            <Button
              style={{ marginLeft: 8 }}
              onClick={() => {
                setIsAddModalVisible(false);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AllUsers;